import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { btnGold, btnLine, inp } from "@/lib/crm";
import { Panel } from "@/pages/Billing";

function ShowroomRow({ s, i, onSaved }) {
  const [f, setF] = useState(s);
  const save = async (patch = {}) => {
    try { await api.put(`/showrooms/${s.id}`, { ...f, ...patch }); toast.success("Showroom saved"); onSaved(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div data-testid={`showroom-row-${i}`} className="grid items-center gap-2 border-t border-slate-800/60 px-6 py-3 md:grid-cols-[1fr_1.4fr_1fr_1fr_auto]">
      {["name", "address", "phone", "email"].map((k) => <input key={k} data-testid={`showroom-${k}-${i}`} value={f[k] || ""} placeholder={k} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inp} h-9`} />)}
      <span className="flex gap-2">
        <button data-testid={`showroom-save-${i}`} onClick={() => save()} className={btnLine}>Save</button>
        {!s.main && <button data-testid={`showroom-toggle-${i}`} onClick={() => save({ active: !s.active })} className={btnLine}>{s.active ? "Deactivate" : "Activate"}</button>}
      </span>
    </div>
  );
}

export function ShowroomsPanel({ onChanged }) {
  const [rooms, setRooms] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/showrooms").then((r) => { setRooms(r.data); onChanged?.(r.data); }).catch(() => {}), [onChanged]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/showrooms", { name: name.trim() }); setName(""); toast.success("Showroom created"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Panel title="Showrooms" testId="showrooms-panel">
      <div className="flex gap-2 px-6 py-4">
        <input data-testid="showroom-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New showroom, e.g. Denver" className={`${inp} w-72`} />
        <button data-testid="showroom-add-button" onClick={add} className={`${btnGold} whitespace-nowrap`}>Add showroom</button>
      </div>
      {rooms.map((s, i) => <ShowroomRow key={`${s.id}-${s.active}`} s={s} i={i} onSaved={load} />)}
    </Panel>
  );
}

export function CrmSettingsPanel() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/crm/settings").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f) return null;
  const save = async () => {
    try { setF((await api.put("/crm/settings", { ...f, deposit_pct: Number(f.deposit_pct), deposit_due_days: Number(f.deposit_due_days), final_due_days: Number(f.final_due_days) })).data); toast.success("Invoice settings saved"); } catch (e) { toast.error(errorMessage(e)); }
  };
  const num = (k, label) => (
    <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-500">{label}
      <input data-testid={`crm-${k}-input`} inputMode="decimal" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inp} font-mono-num`} />
    </label>
  );
  return (
    <Panel title="Invoices" testId="crm-settings-panel">
      <div className="grid gap-3 px-6 py-4 md:grid-cols-5 md:items-end">
        {num("deposit_pct", "Deposit % (if terms have none)")}
        <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-500">Final invoice created
          <select data-testid="crm-final-at-select" value={f.final_at} onChange={(e) => setF({ ...f, final_at: e.target.value })} className={inp}>
            <option value="shipped">At Shipped</option><option value="approved">At Approved (not yet due)</option>
          </select>
        </label>
        {num("deposit_due_days", "Deposit due (days)")}
        {num("final_due_days", "Final due (days)")}
        <button data-testid="crm-settings-save" onClick={save} className={btnGold}>Save</button>
      </div>
    </Panel>
  );
}

export function AssignmentCell({ m, i, rooms, onSaved }) {
  const save = async (patch) => {
    try { await api.post(`/team/${m.id}/assignment`, { crm_role: m.crm_role || "rep", showroom_id: m.showroom_id || rooms[0]?.id, ...patch }); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errorMessage(e)); }
  };
  if (m.role !== "staff") return <span className="text-slate-300">Admin</span>;
  return (
    <span className="flex gap-2">
      <select data-testid={`team-crm-role-${i}`} value={m.crm_role || "rep"} onChange={(e) => save({ crm_role: e.target.value })} className={`${inp} h-9 w-36`}>
        <option value="rep">Sales rep</option><option value="manager">Showroom manager</option>
      </select>
      <select data-testid={`team-showroom-${i}`} value={m.showroom_id || ""} onChange={(e) => save({ showroom_id: e.target.value })} className={`${inp} h-9 w-40`}>
        {rooms.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </span>
  );
}
